import React from 'react';

import { Card, Checkbox, Badge, Tooltip, Button, Link } from '@ergeon/core-components';
import { ClockIcon, UserIcon, BuildingOfficeIcon } from '@ergeon/icons/hero/outline';
import { ArrowUturnLeftIcon } from '@ergeon/icons/hero/micro';

import { formatActivityDateTime, isActivityOverdue, isActivityToday } from '../utils/activityUtils';
import type { Activity } from '../types';

import { getTitleColor, getDateColor, getIconColor, getNameColor } from './activityItemColors';

type ActivityItemProps = {
  activity: Activity;
  href: string;
  disableHover?: boolean;
  onReturnToQueue?: (activityId: number) => void;
};

export const ActivityItem: React.FC<ActivityItemProps> = ({ activity, href, disableHover = false, onReturnToQueue }) => {
  const { title, due_date, due_time, completed, isImportant, user_name, customer_name } = activity;

  const formattedDateTime = formatActivityDateTime(due_date, due_time, completed);
  const isOverdue = isActivityOverdue(activity);
  const isToday = isActivityToday(activity);

  // Checkbox is read-only, onClick required by component but does nothing
  const noOp = () => {
    // No action - checkbox value is controlled by API
  };

  const handleReturnClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onReturnToQueue?.(activity.id);
  };

  const showReturnButton = !completed && !!onReturnToQueue;

  const titleColor = getTitleColor(completed, isOverdue, isToday);
  const dateColor = getDateColor(completed, isOverdue, isToday);
  const iconColor = getIconColor(completed, isOverdue, isToday);
  const nameColor = getNameColor(completed, isOverdue, isToday);

  return (
    <Link className="contents [&>span]:contents" href={href} unstyled>
      <Card
        className="flex lg:flex-row flex-col justify-between lg:items-center gap-4 p-4"
        flavor={completed ? 'regular' : undefined}
        hasHover={!disableHover && !completed}
        hoverType="outline"
        zIndex={completed ? '0' : '2'}>
        <div className="flex flex-row flex-grow justify-between items-center gap-2">
          <div className="flex flex-row justify-between items-center gap-1 w-full">
            <div className="flex flex-row flex-shrink items-center">
              <Checkbox
                checked={completed}
                className="pointer-events-none"
                disabled={completed}
                onClick={noOp}
                size="small" />
              <Tooltip msg={title} position="top">
                <span className={`${titleColor} line-clamp-1 additional-4`}>{title}</span>
              </Tooltip>
            </div>
            {showReturnButton && (
              <Tooltip msg="Return to queue" position="bottom">
                <Button
                  aria-label="Return activity to queue"
                  data-testid="return-to-queue-button"
                  flavor="regular"
                  isSquare
                  onClick={handleReturnClick}
                  size="small"
                  taste="boundless">
                  <ArrowUturnLeftIcon className="size-4" />
                </Button>
              </Tooltip>
            )}
          </div>

          {isImportant && !completed && <Badge flavor="danger" shape="circle" value="Important" />}
        </div>
        <div className="flex flex-grow justify-between items-center gap-4 w-full lg:max-w-[28rem]">
          {customer_name && (
            <Tooltip msg="Customer" position="top">
              <div className="flex items-center gap-1 w-32">
                <BuildingOfficeIcon className={`size-4 shrink-0 ${iconColor}`} />
                <p className={`${nameColor} line-clamp-1 paragraph-3`}>{customer_name}</p>
              </div>
            </Tooltip>
          )}
          {user_name && (
            <Tooltip msg="Assigned User" position="top">
              <div className="flex items-center gap-1 w-32">
                <UserIcon className={`size-4 shrink-0 ${iconColor}`} />
                <p className={`${nameColor} line-clamp-1 paragraph-3`}>{user_name}</p>
              </div>
            </Tooltip>
          )}
          <Tooltip msg="Due Date and Time" position="top">
            <div className="flex flex-grow justify-end lg:justify-end items-center gap-1 w-40">
              <ClockIcon className={`size-4 shrink-0 ${iconColor}`} />
              <p className={`${dateColor} line-clamp-1 paragraph-3`}>{formattedDateTime}</p>
            </div>
          </Tooltip>
        </div>
      </Card>
    </Link>
  );
};

